import { Select } from '@mantine/core';
import React, { useMemo } from 'react';
import { JiraFieldData2 } from '../../../../api/endpoints/jira';

interface JiraOption {
    self: string;
    value: string;
    id: string;
}

interface OptionInputProps {
    field: JiraFieldData2;
    value: JiraOption | null;
    error?: boolean;
    onChange: (value: JiraOption | null) => void;
    onSubmit: (value: JiraOption | null) => void;
}

export const JiraOptionInput = React.memo(({ field, value, error, onChange, onSubmit }: OptionInputProps) => {
    // Transform Jira options to Mantine Select data format
    const options = useMemo(() => {
        if (!field.allowedValues || !Array.isArray(field.allowedValues)) {
            return [];
        }

        return field.allowedValues.map((option: JiraOption) => ({
            value: option.id,
            label: option.value
        }));
    }, [field.allowedValues]);

    // Find the option object that corresponds to the selected value
    const findOptionById = (id: string | null): JiraOption | null => {
        if (!id || !field.allowedValues || !Array.isArray(field.allowedValues)) {
            return null;
        }

        const option = field.allowedValues.find((opt: JiraOption) => opt.id === id);
        return option || null;
    };

    // Handle the Select component's onChange which gives us an ID string
    const handleChange = (selectedId: string | null) => {
        const selectedOption = findOptionById(selectedId);
        onChange(selectedOption);
        onSubmit(selectedOption);
    };

    return (
        <Select
            label={field.name}
            placeholder={`Select ${field.name.toLowerCase()}`}
            required={field.required && !field.hasDefaultValue}
            value={value?.id || null}
            onChange={handleChange}
            data={options}
            clearable={!field.required}
            error={error}
        />
    );
});
